// client/src/coms/views/welcome/welcomePlatform.js
// Smart app promotion for the welcome header: which device we are on, whether Voxemera is already running as an
// app, which builds really exist, and the one download action that fits. Pure functions (no window access) so
// every platform can be tested; readPlatformSignals() gathers the inputs from the browser.
//
// Where the builds come from (nothing is hard-coded, so no obsolete installer can be linked):
//   - Windows: the desktop updater's own manifest, /tauri/latest.json (platforms['windows-x86_64'].url). nginx
//     serves /tauri/ per host from the channel's update folder, so beta.voxemera.com hands out the current Voxemera
//     Beta installer and voxemera.com the current stable one.
//   - macOS, Linux, Android, iOS: an optional /tauri/downloads.json next to it, written by the release process when
//     such a build is published (see DOWNLOADS_FORMAT below). While it is missing those builds are not offered.
// Without a native build a browser gets an honest alternative: installing the web app (PWA) or Safari's Add to
// Home Screen / Add to Dock, always labelled as a web app, never as a native app.
// Inside the desktop app, a native mobile wrapper or an installed web app nothing is promoted.

export const DESKTOP_MANIFEST_URL = '/tauri/latest.json';
export const DOWNLOADS_URL = '/tauri/downloads.json';

// /tauri/downloads.json:
//   { "platforms": {
//       "macos":   { "url": "/tauri/Voxemera Beta_0.1.0_universal.dmg", "version": "0.1.0" },
//       "linux":   { "url": "/tauri/Voxemera Beta_0.1.0_amd64.AppImage", "version": "0.1.0" },
//       "android": { "url": "https://play.google.com/store/apps/details?id=io.pointatlas.voxemera.beta" },
//       "ios":     { "url": "https://apps.apple.com/app/id0000000000" } } }
// Desktop files must be same-site under /tauri/; phone builds may be a same-site .apk or an official store page.
const DESKTOP_FILE = { macos: /\.(dmg|pkg)$/i, linux: /\.(appimage|deb|rpm)$/i };
const STORE_HOSTS = { android: ['play.google.com'], ios: ['apps.apple.com', 'testflight.apple.com'] };

export function detectOs({ userAgent = '', platform = '', uaDataPlatform = '', maxTouchPoints = 0 } = {}) {
  const ua = String(userAgent || '');
  // Chromium's User-Agent Client Hints platform is authoritative when present (iPads never have it: every iPad
  // browser is WebKit, so the touch heuristic below only runs without it).
  const hint = String(uaDataPlatform || '').toLowerCase();
  if (hint) {
    if (hint === 'android') return 'android';
    if (hint === 'windows') return 'windows';
    if (hint === 'macos') return 'mac';
    if (hint === 'chrome os' || hint === 'chromeos') return 'chromeos';
    if (hint === 'linux') return 'linux';
    if (hint === 'ios') return 'ios';
  }
  const p = String(platform || '').toLowerCase();
  if (/android/i.test(ua) || p === 'android') return 'android';
  if (/iphone|ipad|ipod/i.test(ua) || p === 'ios' || p === 'iphone' || p === 'ipad') return 'ios';
  // iPadOS reports itself as a Mac; a touch screen gives it away.
  if ((/macintosh/i.test(ua) || p.startsWith('mac')) && Number(maxTouchPoints) > 1) return 'ios';
  if (/windows/i.test(ua) || p.startsWith('win')) return 'windows';
  if (/cros/i.test(ua) || p === 'chrome os' || p === 'chromeos') return 'chromeos';
  if (/macintosh|mac os x/i.test(ua) || p.startsWith('mac')) return 'mac';
  if (/linux/i.test(ua) || p.startsWith('linux')) return 'linux';
  return 'other';
}

export const isMobileOs = (os) => os === 'android' || os === 'ios';

// runtime:
//   'desktop-app' Voxemera Desktop (Tauri) on a computer
//   'mobile-app'  a native mobile wrapper: Tauri mobile, Capacitor, a React Native / WKWebView shell, a Trusted Web
//                 Activity, or any shell that marks itself with the VoxemeraApp user-agent token or ?vox_runtime=app
//   'installed'   the installed web app (PWA standalone window, iOS home-screen app)
//   'browser'     an ordinary browser tab
// The app checks use objects the runtimes inject themselves, not user-agent guesses.
export function detectRuntime({ os = 'other', isTauri = false, nativeShell = false, twa = false, displayModeStandalone = false, iosStandalone = false, remembered = '' } = {}) {
  if (isTauri) return isMobileOs(os) ? 'mobile-app' : 'desktop-app';
  if (nativeShell || twa || remembered === 'mobile-app') return 'mobile-app';
  if (displayModeStandalone || iosStandalone || remembered === 'installed') return 'installed';
  return 'browser';
}

function sameSitePath(raw, origin) {
  try {
    const u = new URL(String(raw), origin || 'https://invalid.local');
    if (origin && u.origin !== new URL(origin).origin) return '';
    if (!u.pathname.startsWith('/tauri/') || u.pathname.slice(7).includes('/')) return '';
    return u.pathname;
  } catch {
    return '';
  }
}

// The current Windows installer from the updater manifest; only same-site /tauri/ installers are accepted.
export function pickDesktopInstallerUrl(manifest, origin) {
  const raw = manifest && manifest.platforms && manifest.platforms['windows-x86_64'] && manifest.platforms['windows-x86_64'].url;
  if (!raw) return '';
  const path = sameSitePath(raw, origin);
  return path && /-setup\.exe$/i.test(path) ? path : '';
}

export function installerFileName(path, fallback = 'Voxemera setup') {
  try {
    return decodeURIComponent(String(path || '').split('/').pop() || '') || fallback;
  } catch {
    return fallback;
  }
}

// "Voxemera Beta_0.0.8_x64-setup.exe" -> "Voxemera Beta"
export function productNameFromInstaller(path) {
  const name = installerFileName(path, '').split('_')[0].trim();
  return /^voxemera\b/i.test(name) ? name : 'Voxemera';
}

function pickPhoneBuild(entry, kind, origin) {
  const raw = entry && entry.url;
  if (!raw) return null;
  try {
    const u = new URL(String(raw), origin || 'https://invalid.local');
    if (u.protocol === 'https:' && STORE_HOSTS[kind].includes(u.hostname)) return { url: u.href, store: true };
  } catch {
    return null;
  }
  if (kind === 'android') {
    const path = sameSitePath(raw, origin);
    if (path && /\.apk$/i.test(path)) return { url: path, store: false };
  }
  return null;
}

// Every build that really exists: { windows, macos, linux, android, ios }, each null or { url, version, ... }.
export function buildAvailability({ manifest = null, downloads = null, origin = '' } = {}) {
  const out = { windows: null, macos: null, linux: null, android: null, ios: null };
  const win = pickDesktopInstallerUrl(manifest, origin);
  if (win) out.windows = { url: win, version: String((manifest && manifest.version) || ''), product: productNameFromInstaller(win), file: installerFileName(win) };
  const p = (downloads && typeof downloads === 'object' && downloads.platforms) || {};
  for (const key of ['macos', 'linux']) {
    const path = p[key] && p[key].url ? sameSitePath(p[key].url, origin) : '';
    if (path && DESKTOP_FILE[key].test(path)) out[key] = { url: path, version: String(p[key].version || ''), product: productNameFromInstaller(path), file: installerFileName(path) };
  }
  for (const key of ['android', 'ios']) {
    const b = pickPhoneBuild(p[key], key, origin);
    if (b) out[key] = { ...b, version: String((p[key] && p[key].version) || '') };
  }
  return out;
}

const NATIVE = {
  windows: { id: 'windows', label: 'Get Desktop App', menu: 'Windows desktop app', icon: 'windows', platform: 'Windows' },
  macos: { id: 'macos', label: 'Get Mac App', menu: 'Mac app', icon: 'apple', platform: 'macOS' },
  linux: { id: 'linux', label: 'Get Linux App', menu: 'Linux app', icon: 'linux', platform: 'Linux' },
  android: { id: 'android', label: 'Get Android App', menu: 'Android app', icon: 'phone', platform: 'Android' },
  ios: { id: 'ios', label: 'Get iOS App', menu: 'iPhone and iPad app', icon: 'phone', platform: 'iOS' },
};
const OS_BUILD = { windows: 'windows', mac: 'macos', linux: 'linux', android: 'android', ios: 'ios' };

function nativeOption(key, build) {
  const n = NATIVE[key];
  const desktop = key === 'windows' || key === 'macos' || key === 'linux';
  const ver = build.version ? ` ${build.version}` : '';
  const sub = desktop ? `${build.product || 'Voxemera'}${ver} for ${n.platform}` : build.store ? (key === 'ios' ? 'From the App Store' : 'From Google Play') : `Android app${ver}`;
  return { id: n.id, kind: 'native', label: n.label, menu: n.menu, sub, icon: n.icon, url: build.url, file: desktop || !build.store ? build.file || installerFileName(build.url) : '', external: !!build.store };
}

// What the header offers. Returns { primary, others[], style } where
//   primary: the one contextual action (null when this device has nothing worth offering),
//   others:  the remaining real options for the small "more" menu,
//   style:   'desktop' (button in the header bar) or 'phone' (the phone card) for the primary's look.
// Option: { id, kind: 'native' | 'web' | 'help', label, menu, sub, icon, url?, file?, external? }
//   web  = installs this browser's web app (beforeinstallprompt, or the browser-menu steps when not offered)
//   help = opens instructions (Add to Home Screen / Add to Dock / another device)
// `availability` null means the build list is still loading: the Windows button is then shown straight away
// (it is resolved on click) so the header does not jump.
export function appPromotion({ os = 'other', runtime = 'browser', availability = null, canPromptInstall = false, chromium = false, safari = false } = {}) {
  const none = { primary: null, others: [], style: isMobileOs(os) ? 'phone' : 'desktop' };
  if (runtime !== 'browser') return none;
  const av = availability || { windows: os === 'windows' ? { url: '', version: '', product: 'Voxemera', pending: true } : null };
  const mobile = isMobileOs(os);
  const own = OS_BUILD[os];

  // The web-app alternative this browser can actually do.
  let web = null;
  if (os === 'android') web = { id: 'pwa-android', kind: 'web', label: 'Install app', short: 'Install', kicker: 'Web app · no store needed', menu: 'Install the web app', sub: 'Adds Voxemera to this phone from the browser', icon: 'phone' };
  else if (os === 'ios') web = { id: 'ios-home', kind: 'help', label: 'Add to Home Screen', short: 'Add to Home', kicker: 'Web app · on this device', menu: 'Add to Home Screen', sub: 'Use Voxemera like an app from Safari', icon: 'phone' };
  else if (canPromptInstall || chromium) web = { id: 'pwa', kind: 'web', label: 'Install web app', menu: 'Install the web app', sub: 'Its own window, from this browser', icon: 'globe' };
  else if (os === 'mac' && safari) web = { id: 'mac-dock', kind: 'help', label: 'Add to Dock', menu: 'Add to Dock', sub: 'Use Voxemera as a web app from Safari', icon: 'globe' };

  let primary = null;
  if (own && av[own]) primary = av[own].pending ? { ...NATIVE.windows, kind: 'native', sub: 'Voxemera for Windows', url: '', file: '', pending: true } : nativeOption(own, av[own]);
  else if (web) primary = web;

  const others = [];
  for (const key of ['windows', 'macos', 'linux', 'android', 'ios']) {
    if (key === own || !av[key] || av[key].pending) continue;
    // A phone browser is offered the other phone's store page only when it is the same family; desktop builds
    // stay listed (for the user's computer).
    if (mobile && isMobileOs(key)) continue;
    others.push(nativeOption(key, av[key]));
  }
  if (web && primary !== web && !mobile) others.push(web);
  if (!mobile) {
    if (!av.android) others.push({ id: 'android-elsewhere', kind: 'help', label: 'Get the app', menu: 'On an Android phone', sub: 'Install the web app from Chrome', icon: 'phone' });
    if (!av.ios) others.push({ id: 'ios-elsewhere', kind: 'help', label: 'Get the app', menu: 'On an iPhone or iPad', sub: 'Add to Home Screen from Safari', icon: 'phone' });
  }
  return { primary, others, style: mobile ? 'phone' : 'desktop' };
}

// ---------- the one optional install suggestion ----------
// A small dismissible card for returning, signed-out browser visitors on the welcome page only (never inside the
// app, so it cannot interrupt calls, games, Canvas or media). Visits are counted per calendar day; it appears from
// the second day on, at most once per session, never again after the visitor installs or picks a download, and a
// dismissal silences it for NUDGE_COOLDOWN_DAYS (for good after NUDGE_MAX_DISMISSALS).
export const NUDGE_KEY = 'vox_app_promo_v1';
export const NUDGE_COOLDOWN_DAYS = 45;
export const NUDGE_MAX_DISMISSALS = 2;
const DAY = 24 * 60 * 60 * 1000;

export function dayStamp(now) {
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function normaliseNudgeState(raw) {
  const s = raw && typeof raw === 'object' ? raw : {};
  return {
    days: Array.isArray(s.days) ? s.days.filter((d) => typeof d === 'string').slice(-10) : [],
    dismissedAt: Number(s.dismissedAt) || 0,
    dismissals: Number(s.dismissals) || 0,
    acted: !!s.acted,
  };
}

export function recordVisit(state, now) {
  const s = normaliseNudgeState(state);
  const today = dayStamp(now);
  if (s.days[s.days.length - 1] === today) return s;
  return { ...s, days: [...s.days, today].slice(-10) };
}

export function shouldShowNudge(state, { now, signedIn = false, hasPrimary = false, runtime = 'browser' } = {}) {
  const s = normaliseNudgeState(state);
  if (signedIn || !hasPrimary || runtime !== 'browser' || s.acted) return false;
  if (s.days.length < 2) return false;
  if (s.dismissals >= NUDGE_MAX_DISMISSALS) return false;
  if (s.dismissedAt && now - s.dismissedAt < NUDGE_COOLDOWN_DAYS * DAY) return false;
  return true;
}

export function dismissNudge(state, now) {
  const s = normaliseNudgeState(state);
  return { ...s, dismissedAt: now, dismissals: s.dismissals + 1 };
}

export function markActed(state) {
  return { ...normaliseNudgeState(state), acted: true };
}

// ---------- browser signals ----------
const RUNTIME_SESSION_KEY = 'vox_app_runtime';

export function readPlatformSignals() {
  if (typeof window === 'undefined') return { os: 'other', runtime: 'browser', chromium: false, safari: false };
  const nav = window.navigator || {};
  const ua = String(nav.userAgent || '');
  const os = detectOs({
    userAgent: ua,
    platform: nav.platform,
    uaDataPlatform: nav.userAgentData && nav.userAgentData.platform,
    maxTouchPoints: nav.maxTouchPoints,
  });
  let displayModeStandalone = false;
  try {
    displayModeStandalone = !!(
      window.matchMedia &&
      ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'].some((m) => window.matchMedia(`(display-mode: ${m})`).matches)
    );
  } catch {}
  let nativeShell = false;
  try {
    const cap = window.Capacitor;
    nativeShell =
      !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) ||
      !!window.ReactNativeWebView ||
      !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.voxemera) ||
      /\bVoxemeraApp\//.test(ua);
  } catch {}
  let query = '';
  try {
    query = new URLSearchParams(window.location.search || '').get('vox_runtime') || '';
  } catch {}
  const twa = typeof document !== 'undefined' && String(document.referrer || '').startsWith('android-app://');
  // One-shot signals (a TWA's referrer, a wrapper's launch flag) only exist on the first page load, so they are
  // remembered for the rest of the session.
  let remembered = '';
  try {
    remembered = window.sessionStorage.getItem(RUNTIME_SESSION_KEY) || '';
  } catch {}
  const runtime = detectRuntime({
    os,
    isTauri: !!window.__TAURI_INTERNALS__ || !!window.__TAURI__ || window.isTauri === true,
    nativeShell: nativeShell || query === 'app',
    twa,
    displayModeStandalone,
    iosStandalone: nav.standalone === true,
    remembered,
  });
  if ((twa || query === 'app') && runtime === 'mobile-app') {
    try {
      window.sessionStorage.setItem(RUNTIME_SESSION_KEY, 'mobile-app');
    } catch {}
  }
  // Chromium browsers (Chrome, Edge, Opera, Brave…) can install web apps; Firefox cannot, Safari has its own menu.
  const brands = (nav.userAgentData && Array.isArray(nav.userAgentData.brands) && nav.userAgentData.brands) || [];
  const chromium = brands.some((b) => /chromium|google chrome|microsoft edge/i.test(String(b && b.brand))) || (/chrome\/|edg\//i.test(ua) && !/firefox|fxios/i.test(ua));
  const safari = !chromium && /safari\//i.test(ua) && /version\//i.test(ua) && !/firefox|fxios|crios|edgios/i.test(ua);
  return { os, runtime, chromium, safari };
}
